import React, { useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  Button, Box, Typography, Snackbar, Alert, Stack, IconButton, Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';

const CSV_HEADERS = [
  'name', 'email', 'phone', 'status', 'company', 'jobTitle', 'address', 'type', 'source', 'leadScore', 'preferredZip', 'aiIntent', 'website', 'linkedin', 'twitter', 'birthday', 'spouse', 'children', 'budget', 'timeline', 'communication', 'referredBy', 'stage', 'interests', 'language', 'timezone', 'tags', 'notes',
];

function downloadTemplate() {
  const csv = `${CSV_HEADERS.join(',')}\n`;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'contacts-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ContactImport({ onImportSuccess }) {
  const fileInputRef = useRef();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const res = await fetch('/api/v1/contacts/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(results.data),
          });
          const data = await res.json();
          if (data.success) {
            setSnackbar({ open: true, message: `Imported ${data.inserted} contacts!`, severity: 'success' });
            if (onImportSuccess) onImportSuccess();
          } else {
            setSnackbar({ open: true, message: data.error || 'Import failed', severity: 'error' });
          }
        } catch (err) {
          setSnackbar({ open: true, message: 'Import failed', severity: 'error' });
        }
      },
    });
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Import Contacts (CSV)">
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current.click()}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <CloudUploadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download Template">
          <IconButton
            color="primary"
            onClick={downloadTemplate}
            size="small"
            variant="outlined"
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        CSV columns should match your contact fields. Use the template for best results.
      </Typography>
    </Box>
  );
}
